import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import {
    Spade,
    Mail,
    Lock,
    Eye,
    EyeOff,
    Loader2,
    AlertCircle,
    Wallet,
    ListChecks,
    MessageCircle,
    Play,
} from 'lucide-react';
import { auth } from '../lib/firebase';
import { useLanguage } from '../contexts/LanguageContext';
import { LanguageToggle } from '../components/LanguageToggle';

// Map Firebase auth errors to friendly Hebrew copy (falls back gracefully).
const friendlyError = (err) => {
    switch (err?.code) {
        case 'auth/invalid-email':
            return 'כתובת האימייל אינה תקינה';
        case 'auth/user-disabled':
            return 'החשבון הזה הושבת';
        case 'auth/user-not-found':
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
            return 'האימייל או הסיסמה שגויים. נסו שוב';
        case 'auth/too-many-requests':
            return 'יותר מדי ניסיונות. המתינו רגע ונסו שוב';
        case 'auth/network-request-failed':
            return 'בעיית רשת. בדקו את החיבור ונסו שוב';
        default:
            return 'אירעה שגיאה בהתחברות. נסו שוב';
    }
};

const FEATURES = [
    { icon: Wallet, label: 'ניהול קופה חי' },
    { icon: ListChecks, label: 'רשימת המתנה חכמה' },
    { icon: MessageCircle, label: 'סוכן WhatsApp' },
];

export const Login = () => {
    const { t } = useLanguage();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await signInWithEmailAndPassword(auth, email, password);
            // Explicitly navigate after success
            navigate('/dashboard');
        } catch (err) {
            console.error('[Login] Error:', err?.code || err);
            setError(friendlyError(err));
            setLoading(false); // Reset loading so button isn't stuck
        }
    };

    // Auto-redirect if already logged in (standard behavior)
    useEffect(() => {
        if (auth.currentUser) {
            navigate('/dashboard');
        }
    }, [navigate]);

    return (
        <div className="relative min-h-screen w-full overflow-hidden bg-ink-950">
            {/* Language Toggle */}
            <div className="absolute top-5 end-5 z-20">
                <LanguageToggle />
            </div>

            <div className="grid min-h-screen lg:grid-cols-2">
                {/* ---------- Brand hero ---------- */}
                <section className="relative flex items-center justify-center overflow-hidden px-6 pt-24 pb-12 lg:px-12 lg:py-16">
                    {/* Ambient glows */}
                    <div className="pointer-events-none absolute -top-24 -start-24 h-72 w-72 rounded-full bg-felt-500/20 blur-3xl" />
                    <div className="pointer-events-none absolute -bottom-16 end-0 h-80 w-80 rounded-full bg-gold-500/10 blur-3xl" />

                    <div className="relative z-10 flex w-full max-w-lg flex-col items-center text-center lg:items-start lg:text-start">
                        {/* Chip / spade motif */}
                        <div className="mb-8 animate-floaty">
                            <div className="relative grid h-24 w-24 place-items-center rounded-full bg-gradient-to-br from-gold-300 to-gold-600 shadow-[0_12px_44px_-8px_rgba(212,175,55,0.6)]">
                                <div className="absolute inset-2 rounded-full border-2 border-dashed border-ink-950/40" />
                                <Spade className="h-11 w-11 fill-ink-950 text-ink-950" />
                            </div>
                        </div>

                        {/* Wordmark */}
                        <h1 className="font-display text-6xl font-black tracking-tight text-gold-gradient sm:text-7xl">
                            {t('appName')}
                        </h1>

                        {/* Tagline */}
                        <p className="mt-5 max-w-md text-lg leading-relaxed text-slate-300 sm:text-xl">
                            נהלו את משחק הבית שלכם כמו מקצוענים — קופה, מושבים ותשלומים, הכול במקום אחד.
                        </p>

                        {/* WhatsApp highlight */}
                        <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-felt-500/40 bg-felt-500/15 px-4 py-1.5">
                            <MessageCircle className="h-4 w-4 text-felt-400" />
                            <span className="text-sm font-semibold text-felt-50">עכשיו גם דרך WhatsApp</span>
                        </div>

                        {/* Feature bullets */}
                        <ul className="mt-10 flex flex-col gap-4">
                            {FEATURES.map(({ icon: Icon, label }) => (
                                <li key={label} className="flex items-center gap-3">
                                    <span className="grid h-9 w-9 place-items-center rounded-xl border border-felt-500/25 bg-felt-500/15 text-felt-400">
                                        <Icon className="h-4 w-4" />
                                    </span>
                                    <span className="text-slate-300">{label}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>

                {/* ---------- Sign-in card ---------- */}
                <section className="relative flex items-center justify-center px-6 pb-16 pt-4 lg:px-12 lg:py-16">
                    <div className="card-premium w-full max-w-md p-8 sm:p-10">
                        <div className="mb-8 text-center">
                            <h2 className="font-display text-3xl font-bold text-slate-100">התחברות</h2>
                            <p className="mt-2 text-slate-400">התחברו כדי לנהל את המשחק שלכם</p>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-5">
                            {/* Email */}
                            <div>
                                <label htmlFor="email" className="mb-2 block text-sm font-medium text-slate-300">
                                    {t('email')}
                                </label>
                                <div className="relative">
                                    <input
                                        id="email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder={t('email')}
                                        autoComplete="email"
                                        dir="ltr"
                                        className="field peer ps-12 text-start"
                                        required
                                        disabled={loading}
                                    />
                                    <Mail className="pointer-events-none absolute start-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-500 transition-colors peer-focus:text-gold-400" />
                                </div>
                            </div>

                            {/* Password */}
                            <div>
                                <label htmlFor="password" className="mb-2 block text-sm font-medium text-slate-300">
                                    {t('password')}
                                </label>
                                <div className="relative">
                                    <input
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder={t('password')}
                                        autoComplete="current-password"
                                        dir="ltr"
                                        className="field peer ps-12 pe-12 text-start"
                                        required
                                        disabled={loading}
                                    />
                                    <Lock className="pointer-events-none absolute start-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-500 transition-colors peer-focus:text-gold-400" />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword((v) => !v)}
                                        className="absolute end-3 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-slate-500 transition-colors hover:text-gold-400"
                                        aria-label={showPassword ? 'הסתר סיסמה' : 'הצג סיסמה'}
                                        tabIndex={-1}
                                    >
                                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                    </button>
                                </div>
                            </div>

                            {/* Error */}
                            {error && (
                                <div className="flex items-start gap-2.5 rounded-xl border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-300">
                                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                                    <span>{error}</span>
                                </div>
                            )}

                            {/* Submit */}
                            <button type="submit" disabled={loading} className="btn-gold w-full py-3.5 text-base">
                                {loading ? (
                                    <>
                                        <Loader2 className="h-5 w-5 animate-spin" />
                                        מתחבר...
                                    </>
                                ) : (
                                    'התחברות'
                                )}
                            </button>
                        </form>

                        {/* Demo link */}
                        <div className="mt-6 flex items-center justify-center gap-3">
                            <span className="h-px flex-1 bg-white/10" />
                            <button
                                type="button"
                                onClick={() => navigate('/demo')}
                                className="inline-flex items-center gap-1.5 text-sm font-medium text-gold-400 transition-colors hover:text-gold-300"
                            >
                                <Play className="h-3.5 w-3.5" />
                                נסו את הדמו
                            </button>
                            <span className="h-px flex-1 bg-white/10" />
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
};
